import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { ApiError } from "@/lib/api";
import { onTourImageError } from "@/lib/imageFallback";
import { getEvent, formatEventDate, type EventItem } from "@/lib/events";

export default function EventoDetailPage() {
  const { t } = useTranslation("events");
  const { slug } = useParams<{ slug: string }>();
  const [ev, setEv] = useState<EventItem | null>(null);
  const [status, setStatus] = useState<"loading" | "ok" | "notfound">("loading");

  useEffect(() => {
    let cancelled = false;
    if (!slug) return;
    getEvent(slug)
      .then((e) => { if (!cancelled) { setEv(e); setStatus("ok"); document.title = t("events.detailTitleSuffix", { title: e.title }); } })
      .catch((err) => { if (!cancelled) setStatus(err instanceof ApiError && err.status === 404 ? "notfound" : "notfound"); });
    return () => { cancelled = true; };
  }, [slug]);

  if (status === "loading") {
    return <div className="min-h-screen bg-offwhite pt-16 md:pt-20"><div className="h-80 bg-gray-200 animate-pulse" /></div>;
  }
  if (status === "notfound" || !ev) {
    return (
      <div className="min-h-screen bg-offwhite pt-16 md:pt-20 flex items-center justify-center">
        <div className="text-center py-20">
          <p className="text-gray-500 mb-4">{t("events.notFound")}</p>
          <Link to="/eventos" className="text-ocean font-medium hover:underline">{t("events.viewAll")}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-offwhite pt-16 md:pt-20">
      {/* Hero image */}
      <div className="relative h-72 md:h-[28rem] overflow-hidden">
        <img
          src={ev.imageUrl ?? "/brand/hero/i4-1200.jpg"}
          alt={ev.title}
          onError={onTourImageError}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-4xl mx-auto w-full">
            <span className="inline-flex items-center gap-1.5 bg-coral text-white text-xs font-semibold px-3 py-1 rounded-full mb-3">
              <i className="ri-calendar-event-line" /> {formatEventDate(ev)}
            </span>
            <h1 className="font-display text-3xl md:text-5xl font-bold text-white leading-tight">{ev.title}</h1>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
        <div className="max-w-4xl mx-auto">
          <Link to="/eventos" className="text-sm text-ocean hover:underline inline-flex items-center gap-1 mb-6">
            <i className="ri-arrow-left-line" /> {t("events.backToEvents")}
          </Link>

          <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-8">
            <span className="flex items-center gap-1.5"><i className="ri-calendar-line text-ocean" /> {formatEventDate(ev)}</span>
            {ev.startTime && <span className="flex items-center gap-1.5"><i className="ri-time-line text-ocean" /> {ev.startTime.slice(0, 5)}</span>}
            {(ev.location || ev.city) && (
              <span className="flex items-center gap-1.5"><i className="ri-map-pin-line text-ocean" /> {[ev.location, ev.city].filter(Boolean).join(", ")}</span>
            )}
          </div>

          {ev.description && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8">
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{ev.description}</p>
            </div>
          )}

          <div className="flex flex-wrap gap-3 mt-8">
            {ev.linkedTourSlug && (
              <Link
                to={`/tours/${ev.linkedTourSlug}`}
                className="inline-flex items-center gap-2 bg-ocean hover:bg-ocean/90 text-white font-semibold px-6 py-3 rounded-full transition-colors"
              >
                <i className="ri-ticket-2-line" /> {t("events.bookRelated")}
              </Link>
            )}
            {ev.externalUrl && (
              <a
                href={ev.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-gray-200 hover:bg-gray-50 text-charcoal font-semibold px-6 py-3 rounded-full transition-colors"
              >
                <i className="ri-external-link-line" /> {t("events.moreInfo")}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
